import { useEffect } from 'react';

import { formatPhoneE164ToDisplay } from 'components/CallScribe/Recents/recentsHelpers';
import { isSameNumber } from 'components/CallScribe/softphoneLiveCall';

export const LEASE_KEY = 'mspp.callscribe.liveCallLease';
export const LIVE_CALL_LOCK = 'mspp-callscribe-live-call';
export const LEASE_REFRESH_MS = 2000;
export const LEASE_TTL_MS = 6000;
export const LEASE_NOTE_TTL_MS = 180_000;

const CHANNEL_NAME = 'mspp.callscribe.liveCallLease';
const WINDOW_ID_KEY = 'mspp.callscribe.windowId';

export interface ILiveCallLease {
  windowId: string;
  callId: number | null;
  callee: string;
  toNumber: string;
  at: number;
}

export type TLiveCall = Pick<ILiveCallLease, 'callId' | 'callee' | 'toNumber'>;

let fallbackWindowId: string | null = null;

const newWindowId = (): string =>
  Array.from(crypto.getRandomValues(new Uint8Array(8)), b => b.toString(16).padStart(2, '0')).join('');

export const thisWindowId = (): string => {
  try {
    const existing = window.sessionStorage.getItem(WINDOW_ID_KEY);
    if (existing) return existing;
    const id = newWindowId();
    window.sessionStorage.setItem(WINDOW_ID_KEY, id);
    return id;
  } catch {
    fallbackWindowId = fallbackWindowId ?? newWindowId();
    return fallbackWindowId;
  }
};

const parseLease = (raw: string | null): ILiveCallLease | null => {
  if (!raw) return null;
  try {
    const lease = JSON.parse(raw) as Partial<ILiveCallLease> | null;
    if (
      typeof lease?.windowId !== 'string' ||
      typeof lease.toNumber !== 'string' ||
      typeof lease.callee !== 'string' ||
      typeof lease.at !== 'number'
    ) {
      return null;
    }
    return {
      windowId: lease.windowId,
      callId: typeof lease.callId === 'number' ? lease.callId : null,
      callee: lease.callee,
      toNumber: lease.toNumber,
      at: lease.at,
    };
  } catch {
    return null;
  }
};

const isStaleNote = (lease: ILiveCallLease, now: number): boolean =>
  now - lease.at >= LEASE_NOTE_TTL_MS || lease.at - now >= LEASE_NOTE_TTL_MS;

const storedLease = (): ILiveCallLease | null => {
  try {
    const lease = parseLease(window.localStorage.getItem(LEASE_KEY));
    if (lease && isStaleNote(lease, Date.now())) {
      window.localStorage.removeItem(LEASE_KEY);
      return null;
    }
    return lease;
  } catch {
    return null;
  }
};

export const readLiveCallHolder = (): ILiveCallLease | null => {
  const lease = storedLease();
  return lease && lease.windowId !== thisWindowId() ? lease : null;
};

export const liveCallHeldMessage = (holder: ILiveCallLease | null): string => {
  const name = holder
    ? holder.callee.trim() || formatPhoneE164ToDisplay(holder.toNumber) || holder.toNumber
    : '';
  return name
    ? `You're on a call with ${name} in your other CallScribe window.`
    : 'You\'re on a call in your other CallScribe window.';
};

export const holderNameFor = (holder: ILiveCallLease | null, toNumber: string): string | null => {
  if (!holder || !isSameNumber(holder.toNumber, toNumber)) return null;
  const name = holder.callee.trim();
  if (!name || name === holder.toNumber || name === formatPhoneE164ToDisplay(holder.toNumber)) return null;
  return name;
};

export const watchLiveCallHolder = (onChange: (holder: ILiveCallLease) => void): (() => void) => {
  const onStorage = (event: StorageEvent): void => {
    if (event.key !== LEASE_KEY || event.newValue === null) return;
    const holder = readLiveCallHolder();
    if (holder) onChange(holder);
  };
  window.addEventListener('storage', onStorage);
  return () => { window.removeEventListener('storage', onStorage); };
};

export const readOtherLiveLease = (now: number = Date.now()): ILiveCallLease | null => {
  const lease = storedLease();
  if (!lease || lease.windowId === thisWindowId()) return null;
  if (now - lease.at >= LEASE_TTL_MS || lease.at - now >= LEASE_TTL_MS) return null;
  return lease;
};

const openChannel = (): BroadcastChannel | null => {
  try {
    return typeof BroadcastChannel === 'function' ? new BroadcastChannel(CHANNEL_NAME) : null;
  } catch {
    return null;
  }
};

const announce = (message: { type: string }): void => {
  const channel = openChannel();
  if (!channel) return;
  try {
    channel.postMessage(message);
  } catch {
    return undefined;
  } finally {
    channel.close();
  }
};

const writeMeta = (meta: TLiveCall): void => {
  try {
    window.localStorage.setItem(LEASE_KEY, JSON.stringify({ windowId: thisWindowId(), ...meta, at: Date.now() }));
  } catch {
    return undefined;
  }
};

const clearOwnMeta = (): void => {
  try {
    if (storedLease()?.windowId === thisWindowId()) window.localStorage.removeItem(LEASE_KEY);
  } catch {
    return undefined;
  }
};

const lockManager = (): LockManager | null => {
  try {
    const manager = (navigator as Navigator & { locks?: LockManager }).locks;
    return manager && typeof manager.request === 'function' ? manager : null;
  } catch {
    return null;
  }
};

interface IHeldLock {
  meta: TLiveCall;
  release: (() => void) | null;
  heartbeat: number;
}

let held: IHeldLock | null = null;
let pending: Promise<boolean> | null = null;

export const holdsLiveCallLock = (): boolean => held !== null;

export const releaseLiveCallLock = (): void => {
  if (held === null) return;
  const { release, heartbeat } = held;
  held = null;
  window.clearInterval(heartbeat);
  window.removeEventListener('pagehide', releaseLiveCallLock);
  clearOwnMeta();
  release?.();
  announce({ type: 'released' });
};

const hold = (meta: TLiveCall, release: (() => void) | null): void => {
  held = {
    meta,
    release,
    heartbeat: window.setInterval(() => { if (held) writeMeta(held.meta); }, LEASE_REFRESH_MS),
  };
  writeMeta(meta);
  window.addEventListener('pagehide', releaseLiveCallLock);
};

export const forgetLiveCallLease = (): void => {
  releaseLiveCallLock();
  try {
    window.localStorage.removeItem(LEASE_KEY);
  } catch {
    return undefined;
  }
};

const holdWithoutLocks = (meta: TLiveCall): boolean => {
  if (readOtherLiveLease()) return false;
  hold(meta, null);
  return true;
};

const requestLock = (manager: LockManager, meta: TLiveCall): Promise<boolean> =>
  new Promise<boolean>(resolve => {
    manager
      .request(LIVE_CALL_LOCK, { ifAvailable: true }, lock => {
        if (!lock) {
          resolve(false);
          return undefined;
        }
        return new Promise<void>(_resolve => {
          hold(meta, _resolve);
          resolve(true);
        });
      })
      .catch(() => { resolve(holdWithoutLocks(meta)); });
  });

export const updateLiveCallMeta = (meta: TLiveCall): void => {
  if (held === null) return;
  held.meta = meta;
  writeMeta(meta);
};

export const acquireLiveCallLock = (meta: TLiveCall): Promise<boolean> => {
  if (pending) return pending.then(ok => (ok ? acquireLiveCallLock(meta) : false));
  if (held) {
    updateLiveCallMeta(meta);
    return Promise.resolve(true);
  }
  const manager = lockManager();
  if (!manager) return Promise.resolve(holdWithoutLocks(meta));
  const request = requestLock(manager, meta).finally(() => { pending = null; });
  pending = request;
  return request;
};

export const liveCallHeldElsewhere = async (): Promise<boolean> => {
  if (held) return false;
  const manager = lockManager();
  if (!manager || typeof manager.query !== 'function') return readOtherLiveLease() !== null;
  try {
    const state = await manager.query();
    return (state.held ?? []).some(lock => lock.name === LIVE_CALL_LOCK);
  } catch {
    return readOtherLiveLease() !== null;
  }
};

export const whenLiveCallEnds = (onEnded: () => void): (() => void) => {
  let done = false;
  const fire = (): void => {
    if (done) return;
    done = true;
    onEnded();
  };
  const manager = lockManager();
  if (manager) {
    const controller = new AbortController();
    manager
      .request(LIVE_CALL_LOCK, { signal: controller.signal }, () => {
        if (!controller.signal.aborted) fire();
        return undefined;
      })
      .catch(() => undefined);
    return () => {
      done = true;
      controller.abort();
    };
  }
  const channel = openChannel();
  const onMessage = (event: MessageEvent): void => {
    if ((event.data as { type?: string } | null)?.type === 'released') fire();
  };
  const onStorage = (event: StorageEvent): void => {
    if (event.key === LEASE_KEY && event.newValue === null) fire();
  };
  channel?.addEventListener('message', onMessage);
  window.addEventListener('storage', onStorage);
  const timer = window.setInterval(() => {
    if (readOtherLiveLease() === null) fire();
  }, LEASE_REFRESH_MS);
  return () => {
    done = true;
    channel?.removeEventListener('message', onMessage);
    channel?.close();
    window.removeEventListener('storage', onStorage);
    window.clearInterval(timer);
  };
};

export const askLiveWindowToFocus = (): void => {
  const channel = openChannel();
  if (!channel) return;
  try {
    channel.postMessage({ type: 'focus' });
  } catch {
    return undefined;
  } finally {
    channel.close();
  }
};

export const useLiveCallLease = (live: TLiveCall | null): void => {
  const toNumber = live?.toNumber ?? null;
  const callee = live?.callee ?? '';
  const callId = live?.callId ?? null;

  useEffect(() => {
    if (toNumber === null) return undefined;
    updateLiveCallMeta({ callId, callee, toNumber });

    const channel = openChannel();
    const onMessage = (event: MessageEvent): void => {
      const data = event.data as { type?: string } | null;
      if (data?.type !== 'focus') return;
      try {
        window.focus();
      } catch {
        return undefined;
      }
    };
    channel?.addEventListener('message', onMessage);

    return () => {
      channel?.removeEventListener('message', onMessage);
      channel?.close();
    };
  }, [toNumber, callee, callId]);
};
